import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  Output,
  EventEmitter,
} from '@angular/core';

import { Subject, Subscription, distinctUntilChanged, firstValueFrom, lastValueFrom, skip, takeUntil } from 'rxjs';
import { BackendApiService } from 'src/app/core/services/backend-api';
import {
  CurrentWorkFlowHistory,
  WorkflowRecord,
} from 'src/app/shared/models/database-models';

import { Store } from '@ngrx/store';
import {
  ExtendedKeycloakUser,
  IItemToRequest,
  MyPurchaseForm,
  StoreDropdowns,
  UserInformation,
} from 'src/app/shared/models/app-models';
import { BudgetWorkFlowHistory, GeneratedWorkFlowHistory } from '../../../shared/models/database-models/WorkflowHistory.model';
import { ActivatedRoute } from '@angular/router';
import { RFSReady, RfsService } from '../../mypurchase-form/rfs/rfs.service';
import { CurrentWorkflowService } from './current-workflow.service';
import { decrementLoading, incrementLoading } from 'src/app/core/store/loading.action';
import { LoadingState } from 'src/app/core/store/loading.reducer';

@Component({
  selector: 'app-current-workflow',
  templateUrl: './current-workflow.component.html',
  styleUrls: ['./current-workflow.component.scss'],
})
export class CurrentWorkflowComponent implements OnChanges, OnDestroy {
  @Input() MpNumber: string | null = '';
  @Input() isRFSReady: boolean = false;
  @Input() isGenerateWorkflowError: boolean = false;
  @Output() userAction = new EventEmitter();
  @Input() updatedDataSource: GeneratedWorkFlowHistory | null = null;
  workflowSubscription!: Subscription;
  timestamp?: Date;
  displayedColumns: string[] = [
    'personel',
    'appointment',
    'status',
    'date',
    'comment',
  ];
  dataSource: CurrentWorkFlowHistory = {
    CEAssignment: [],
    budget: [],
    woc: [],
    quotation: [],
    tenderDocument: [],
    tenderDocumentApproval: [],
    sow: [],
    rfs: [],
    vendor: [],
  };
  destroy$: Subject<boolean> = new Subject<boolean>();
  KCUserList: ExtendedKeycloakUser[] = [];
  itemList: IItemToRequest[] = [];
  currentUser: string = '';
  purchaseType: string = '';
  purchaseTypeId: number = 0;
  EstimatedItemAmount = 0;
  isLoadingData: boolean = false;
  private loadingSubscription: Subscription = new Subscription();
  isLoading = false;

  constructor(
    private backEndService: BackendApiService,
    private route: ActivatedRoute,
    private store: Store<{ dp: StoreDropdowns; user: UserInformation; form: MyPurchaseForm,  loading: LoadingState }>,
    private rfsService: RfsService,
    private currentWorkflowService: CurrentWorkflowService
  ) {
    //this code won't work breaks the ao page.
    //this.purchaseType = this.route.snapshot.params['purchaseType'].split(';')[0] as string;
    this.loadingSubscription = this.store.select('loading').subscribe((loadingState) => {
      this.isLoading = loadingState.loadingStates['currentWorkflow'] > 0 && loadingState.loadingStates['generateWorkflow'] > 0;
    });
    this.store
      .select('form')
      .pipe(takeUntil(this.destroy$))
      .subscribe((form: MyPurchaseForm) => {
        //5 = WOC-DOTP
        this.purchaseTypeId = form.PurchaseTypeId ? form.PurchaseTypeId : 0;
        if (form.PurchaseTypeId == 5) {
          this.itemList = form.ItemList || [];
          this.calculateTotalAmount(this.itemList);
        }
      });
    this.store
      .select('dp')
      .pipe(takeUntil(this.destroy$))
      .subscribe((dp: StoreDropdowns) => {
        this.KCUserList = dp.UserList as ExtendedKeycloakUser[];
      });
    this.store
      .select('user')
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: UserInformation) => {
        this.currentUser = user.userName;
      });

    this.rfsService.rfsReady$
      // only fire observable change when data change
      .pipe(distinctUntilChanged((prev, curr) => prev.data == curr.data))
      .pipe(takeUntil(this.destroy$))
      .subscribe((rfsReady: RFSReady) => {
        // call onFetchWorkflowHistory when rfsReady.isLoading is false
        if (rfsReady.isLoading) {
          this.onFetchWorkflowHistory();
        }
      });
  }

  async ngOnChanges(changes: SimpleChanges) {
    // trigger only when MpNumber change and not a first change
    // bryan note - not really needed as onFetchWorkflowHistory is already
    // handled in this.rfsService.rfsReady$
    if (changes['MpNumber'] && !changes['MpNumber'].firstChange) {
      this.onFetchWorkflowHistory();
    }

    if (changes['updatedDataSource'] && this.updatedDataSource !== null) {
      // let finalData = this.convertToNamesFromData(this.updatedDataSource);
      this.dataSource = this.updatedDataSource;
      if (this.updatedDataSource.TimeStamp) {
        let newDate = new Date(this.updatedDataSource.TimeStamp)
        this.timestamp = newDate
      }
    }
  }

  ngOnInit() {
    // Subscribe to the BehaviorSubject observable
    this.workflowSubscription = this.currentWorkflowService.workflow$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data) {
          this.store.dispatch(incrementLoading({ context: 'currentWorkflow' }));
          this.dataSource = data;
          if (data.TimeStamp) {
            let newDate = new Date(data.TimeStamp)
            this.timestamp = newDate
          }
          this.store.dispatch(decrementLoading({ context: 'currentWorkflow' }));
        } else {
          this.dataSource = {
            CEAssignment: [],
            budget: [],
            woc: [],
            quotation: [],
            tenderDocument: [],
            tenderDocumentApproval: [],
            sow: [],
            rfs: [],
            vendor: []
          } as CurrentWorkFlowHistory;
        }
      }
    );
    this.onFetchWorkflowHistory()
  }

  async onFetchWorkflowHistory() {
    try {
      this.isLoadingData = true
      await this.getWorkflowHistory();
    } catch (e) {
      console.log('error', e);
    } finally {
      this.isLoadingData = false
    }
  }

  calculateTotalAmount(itemList: any) {
    // Initialize total price
    let totalPrice = 0;
    // Calculate total EstimateUnitPrice
    for (let i = 0; i < itemList.length; i++) {
      if (!itemList[i].isDeleted && !itemList[i].Strikethrough) {
        totalPrice += (itemList[i].ItemPrice.UnitPrice * itemList[i].ItemPrice.Quantity) / parseInt(itemList[i].ItemPrice.UnitsPerQuantity);
      }
    }
    this.EstimatedItemAmount = totalPrice;
  }

  async getWorkflowHistory() {
    if (this.KCUserList.length == 0) {
      this.KCUserList = (
        await firstValueFrom(this.store.select('dp').pipe(skip(1)))
      ).UserList as ExtendedKeycloakUser[];
    }
    if (this.MpNumber) {
      try {
        console.log('validating....')
        let data = await lastValueFrom(
          this.backEndService
            .getCurrentWorkflowHistory(this.MpNumber as string)
            .pipe(takeUntil(this.destroy$))
        );

        console.log(data, "data")
        let generatedWorkflowData = data as GeneratedWorkFlowHistory
        if (generatedWorkflowData.TimeStamp) {
          let newDate = new Date(generatedWorkflowData.TimeStamp)
          this.timestamp = newDate
        }
        else this.timestamp = undefined
        this.getCurrentUserAction(data);
        //Below line is to add rfs key in object
        data.rfs = data.rfs ? data.rfs : [];
        data.quotation = data.quotation ? data.quotation : [];
        // let finalData = this.convertToNamesFromData(data);
        // if(data.woc && data.woc.length > 0){
        //   let wocApproved=false;
        //   wocApproved =data.woc[data.woc.length -1].Action === 'Approved';
        //   this.currentWorkflowService.setisWocApproved(wocApproved);
        // }
        this.dataSource = data;
      } catch (e) {
        console.log(e);
        throw Error('Error in record');
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
    if (this.workflowSubscription) {
      this.workflowSubscription.unsubscribe();
    }
  }

  getNameOfUser(username: string) {
    return this.KCUserList.find((user) => user.username == username)?.value ?? username;
  }

  //not in use (using CoveringName, PersonnelName)
  convertToNamesFromData(data: CurrentWorkFlowHistory) {
    // budget
    for (const budget of data.budget) {
      for (const record of budget.recordList) {
        record.Personnel = this.getNameOfUser(record.Personnel);
        if (record.Covering) record.Covering = this.getNameOfUser(record.Covering!);
      }
    }

    // woc
    this.convertToNamesHelper(data.woc);
    // rfs
    this.convertToNamesHelper(data.rfs);
    //ce
    this.convertToNamesHelper(data.CEAssignment);
    //sow
    this.convertToNamesHelper(data.sow);
    //tender
    this.convertToNamesHelper(data.tenderDocument);
    this.convertToNamesHelper(data.tenderDocumentApproval);
    return data
  }

  //not in use (using CoveringName, PersonnelName)
  convertToNamesHelper(records: WorkflowRecord[]) {
    for (const record of records) {
      record.Personnel = this.getNameOfUser(record.Personnel);
      if (record.Covering && record.Covering.length > 0) record.Covering = this.getNameOfUser(record.Covering)
    }
  }

  getCurrentUserAction(data: CurrentWorkFlowHistory) {
    const workflowHistory = structuredClone(data)
    const budgetHistories = workflowHistory.budget
      ?.filter((item) => {
        return item.recordList?.some(
          (r) =>
            r.Personnel === this.currentUser || r.Covering === this.currentUser
        );
      })
      .map((history) => {
        history.recordList = history.recordList.filter(
          (r) =>
            r.Personnel === this.currentUser || r.Covering === this.currentUser
        );
        return history;
      });

    const restHistoies = Object.keys(workflowHistory).reduce(
      (all, key) => {
        if (key !== 'budget' && key != 'TimeStamp') {
          const histories = (
            workflowHistory[
            key as keyof CurrentWorkFlowHistory
            ] as WorkflowRecord[]
          ).filter(
            (r) =>
              r.Personnel === this.currentUser ||
              r.Covering === this.currentUser
          );
          if (histories?.length) {
            all[key as keyof Omit<CurrentWorkFlowHistory, 'budget'>] =
              histories;
          }
        }
        return all;
      },
      {} as Partial<CurrentWorkFlowHistory>
    );
    this.userAction.emit({ budget: budgetHistories, ...restHistoies });
  }
}

export interface ApprovalHistoryType {
  group: string;
  type: string;
  recordList: ApprovalHistory[];
}

export interface ApprovalHistory {
  Personnel: string;
  Appointment: string;
  Action: string;
  Date: Date;
  Comment: string | null;
}
